import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type HeartRateResponse } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { HrStat } from "./_shared";

const ACCENT = "#9a3412";

const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function HeartOverview() {
  const [data, setData] = useState<HeartRateResponse | null | "error">(null);

  useEffect(() => {
    api.heart
      .overview(14)
      .then(setData)
      .catch(() => setData("error"));
  }, []);

  const hr = data && data !== "error" ? data : null;
  const today = hr?.today ?? null;
  const intraday = hr?.intraday ?? [];
  const daily = hr?.daily ?? [];
  const nothing = hr && !today && intraday.length === 0 && daily.length === 0;

  return (
    <div className="animate-fade-in">
      <div className="grid grid-cols-4 gap-2">
        <HrStat label="Resting" value={today?.resting} unit="bpm" accent />
        <HrStat label="Average" value={today?.avg} unit="bpm" />
        <HrStat label="Min" value={today?.min} unit="bpm" />
        <HrStat label="Max" value={today?.max} unit="bpm" />
      </div>
      {today != null && (
        <p className="mt-2 text-xs text-ink-subtle">
          {today.readings} reading{today.readings === 1 ? "" : "s"} today, in 5-minute averages.
        </p>
      )}

      {nothing ? (
        <EmptyState error={data === "error"} title="No heart-rate data yet">
          Once your Galaxy Watch heart rate flows in through Health Connect, today's curve shows
          up here.
        </EmptyState>
      ) : (
        <>
          {intraday.length > 0 && (
            <section className="mt-6">
              <h2 className="section-title mb-3">Today</h2>
              <div className="card card-pad">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={intraday}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                    <XAxis dataKey="t" tickFormatter={hhmm} axisLine={false} tickLine={false} fontSize={11} minTickGap={40} />
                    <YAxis axisLine={false} tickLine={false} width={32} fontSize={11} domain={["auto", "auto"]} />
                    <Tooltip labelFormatter={(v) => hhmm(String(v))} formatter={(value: number) => [`${value} bpm`, "Heart rate"]} />
                    <Line type="monotone" dataKey="bpm" stroke={ACCENT} strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          {daily.length > 1 && (
            <section className="mt-6">
              <h2 className="section-title mb-3">Recent average</h2>
              <div className="card card-pad">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={24} />
                    <YAxis axisLine={false} tickLine={false} width={32} fontSize={11} domain={["auto", "auto"]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="avg_hr" name="Avg" stroke={ACCENT} strokeWidth={2} dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
